import {useContext, useEffect, useState} from "react";
import {DutyManagerReportEditableContext} from "../../../Context/DutyManagerReportEditable";
import {useDispatch, useSelector} from "react-redux";
import React from "react";
import {RootStore} from "../../../../../../../../store/Store";
import {formatUnixToHHmmddd} from "../../../../../../../../utils/momentUtils";
import {BillingType, CalendarEntry, StaffAssignment} from "../../../../../../../../api/grs";
import {setStaffAssignment} from "../../../../../../../../store/calendarEntry/actions/CalendarEntryActions";
import CalendarTimeDropdown from "../../../../../../../Dropdown/Components/CalendarTimeDropdown";
import {getUiFriendlyClinicalGrade} from "../../../../../../../../utils/textUtils";
import GenericTypeDropdown from "../../../../../../../Dropdown/Components/GenericTypeDropdown";
import {
    canUserEditRate,
    validateRate,
    validateRateInput
} from "../../../../../../AdminCalendarEntry/Components/Admin/StaffManagement/Blocks/Assignment/Helpers/staffAssignmentHelpers";
import {showErrorToast} from "../../../../../../../../utils/toastUtils";
import {DutyManagerReportFormErrorsContext} from "../../../Context/DutyManagerReportFormErrors";
import {useIsPrivilegedUser} from "../../../../../../../../hooks/useIsPrivilegedUser";

const FormTimesheetItem = (props: StaffAssignment) => {
    const errorsContext = useContext(DutyManagerReportFormErrorsContext);
    const dispatch = useDispatch();
    const staffList = useSelector((state: RootStore) => state.staffList).data ?? [];
    const entry = useSelector((state: RootStore) => state.calendarEntry).data;
    const venue = useSelector((state: RootStore) => state.venue);
    const [allowRateChange, setAllowRateChange] = useState(false);
    const isPrivilegedUser = useIsPrivilegedUser();
    const canChangeRate = allowRateChange || isPrivilegedUser;
    const editable = useContext(DutyManagerReportEditableContext);

    const staff = staffList.find((s) => s.username == props.staffMember?.staffId);

    const onRateTypeSelected = (id: number | string) => {
        const billingType =
            id == EndOfShiftReportBillingType.Default ? undefined : (id as BillingType);
        dispatch(
            setStaffAssignment({
                ...props,
                billingType
            })
        );
    };

    const onRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = event.target;
        if (!validateRateInput(value)) return;

        dispatch(
            setStaffAssignment({
                ...props,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                rate: value
            })
        );
    };

    const onRateBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = event.target;
        const validation = validateRate(+value);

        errorsContext.setFormErrors({
            ...errorsContext.formErrors,
            rate: validation
        });

        if (!validation.valid) {
            for (const error of validation.messages) {
                showErrorToast(error);
            }
            return;
        }
    };

    useEffect(() => {
        if (props.rate) return;
        const canEditRate = canUserEditRate(venue.data, staff?.clinicalGrade);
        setAllowRateChange(canEditRate);
    }, [props.rate, venue.data, staff?.clinicalGrade]);

    if (!entry || !staff) return null;

    return (
        <>
            <div className="eos-timesheet-name">
                {props.staffMember?.staffName}
                <span className="supplemental">, {staff.clinicalGrade}</span>
            </div>
            <div className="eos-timesheet-grade">
                {getUiFriendlyClinicalGrade(staff.clinicalGrade)}
            </div>

            {editable && canChangeRate ? (
                <GenericTypeDropdown
                    enumOptions={EndOfShiftReportBillingType}
                    splitByCapitalLetter={false}
                    clearable={false}
                    searchable={false}
                    changeOption={onRateTypeSelected}
                    id={props.billingType ?? EndOfShiftReportBillingType.Default}
                    disabled={!editable}
                />
            ) : (
                <div>{props.billingType ?? EndOfShiftReportBillingType.Default}</div>
            )}

            <input
                className="eos-rate-input"
                type="text"
                value={props.rate}
                onChange={onRateChange}
                onBlur={onRateBlur}
                disabled={!editable || !canChangeRate}
                placeholder={"Enter staff rate..."}
            />

            {props.billingType == BillingType.Hourly || props.billingType == BillingType.Daily ? (
                <HourlyTimes editable={editable} entry={entry} staff={props} />
            ) : (
                <>
                    <div className="eos-time-only"></div>
                    <div className="eos-time-only"></div>
                </>
            )}
        </>
    );
};

export default FormTimesheetItem;

// eslint-disable-next-line no-shadow
enum EndOfShiftReportBillingType {
    Default = "Default",
    Hourly = "Hourly",
    Daily = "Daily"
}

function HourlyTimes(props: HourlyTimesProps) {
    const dispatch = useDispatch();
    const startTime = props.staff.startDate ?? props.entry.actualStartDate ?? props.entry.startDate;
    const endTime = props.staff.endDate ?? props.entry.actualEndDate ?? props.entry.endDate;

    if (!startTime || !endTime) return null;

    const onStartTimeSelected = (time?: number | string) => {
        // If startDate is set then endDate must also be set
        dispatch(
            setStaffAssignment({
                ...props.staff,
                startDate: time ? +time : undefined,
                endDate: time
                    ? props.staff.endDate ?? props.entry.actualEndDate ?? props.entry.endDate
                    : undefined
            })
        );
    };

    const onEndTimeSelected = (time?: number | string) => {
        // If endDate is set then startDate must also be set
        dispatch(
            setStaffAssignment({
                ...props.staff,
                endDate: time ? +time : undefined,
                startDate: time
                    ? props.staff.startDate ?? props.entry.actualStartDate ?? props.entry.startDate
                    : undefined
            })
        );
    };
    return (
        <>
            <div className="eos-time">
                {props.editable ? (
                    <CalendarTimeDropdown
                        entry={props.entry}
                        id={startTime}
                        optionsPerHour={4}
                        clearable={false}
                        disabled={!props.editable}
                        daysToGenerate={1}
                        changeOption={onStartTimeSelected}
                        isSearchable={props.editable}
                    />
                ) : (
                    <div>{formatUnixToHHmmddd(startTime)}</div>
                )}
            </div>
            <div className="eos-time">
                {props.editable ? (
                    <CalendarTimeDropdown
                        entry={props.entry}
                        id={endTime}
                        optionsPerHour={4}
                        clearable={false}
                        disabled={!props.editable}
                        daysToGenerate={1}
                        changeOption={onEndTimeSelected}
                        isSearchable={props.editable}
                    />
                ) : (
                    <div>{formatUnixToHHmmddd(endTime)}</div>
                )}
            </div>
        </>
    );
}

interface HourlyTimesProps {
    editable: boolean;
    entry: CalendarEntry;
    staff: StaffAssignment;
}
